/**
 * VerificationStepTwo Component
 *
 * Document upload for verification (Step 2):
 * - Agency License (宅地建物取引業者免許証)
 * - Responsible Officer ID (宅地建物取引士証)
 *
 * Now supports real file uploads with FileUpload component.
 */

import { useState, useCallback } from "react";

// Hooks
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";
import { useTranslation } from "@/shared/hooks";

// Components
import { FileUpload, type UploadedFile } from "@/shared/ui/components";

// API
import { uploadVerificationDocument, deleteFile } from "@/shared/api/uploadApi";

/**
 * Step 2 form data (document upload)
 */
export interface StepTwoData {
  agencyLicenseUrl: string;
  officerIdUrl: string;
}

/**
 * Props for VerificationStepTwo
 */
interface VerificationStepTwoProps {
  data: StepTwoData;
  onChange: (field: keyof StepTwoData, value: string) => void;
}

/**
 * VerificationStepTwo Component
 */
export const VerificationStepTwo = observer(function VerificationStepTwo({
  data,
  onChange,
}: VerificationStepTwoProps) {
  const t = useTranslation();
  const { authStore } = useStore();
  const { user } = authStore;

  const [agencyLicenseUploading, setAgencyLicenseUploading] = useState(false);
  const [officerIdUploading, setOfficerIdUploading] = useState(false);

  /**
   * Convert URL string to UploadedFile array
   */
  const urlToUploadedFile = useCallback(
    (url: string, filename: string): UploadedFile[] => {
      if (!url) return [];
      return [
        {
          url,
          filename,
          size: 0, // Unknown size for existing files
          mimeType: url.toLowerCase().endsWith(".pdf")
            ? "application/pdf"
            : "image/jpeg",
        },
      ];
    },
    [],
  );

  /**
   * Handle agency license upload
   */
  const handleAgencyLicenseUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!user || !file) return;

      setAgencyLicenseUploading(true);
      try {
        const uploaded = await uploadVerificationDocument(
          file,
          user.uid,
          "license",
        );
        onChange("agencyLicenseUrl", uploaded.url);
      } catch (error) {
        console.error("Failed to upload agency license:", error);
        throw error;
      } finally {
        setAgencyLicenseUploading(false);
      }
    },
    [user, onChange],
  );

  /**
   * Handle agency license delete
   */
  const handleAgencyLicenseDelete = useCallback(
    async (fileUrl: string) => {
      await deleteFile(fileUrl);
      onChange("agencyLicenseUrl", "");
    },
    [onChange],
  );

  /**
   * Handle officer ID upload
   */
  const handleOfficerIdUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!user || !file) return;

      setOfficerIdUploading(true);
      try {
        const uploaded = await uploadVerificationDocument(
          file,
          user.uid,
          "officer_id",
        );
        onChange("officerIdUrl", uploaded.url);
      } catch (error) {
        console.error("Failed to upload officer ID:", error);
        throw error;
      } finally {
        setOfficerIdUploading(false);
      }
    },
    [user, onChange],
  );

  /**
   * Handle officer ID delete
   */
  const handleOfficerIdDelete = useCallback(
    async (fileUrl: string) => {
      await deleteFile(fileUrl);
      onChange("officerIdUrl", "");
    },
    [onChange],
  );

  return (
    <div className="space-y-6">
      {/* Agency License Upload */}
      <div>
        <FileUpload
          mode="document"
          multiple={false}
          maxSize={15 * 1024 * 1024} // 15MB
          label={t("verificationAgencyLicense")}
          helperText={t("verificationAgencyLicenseHint")}
          value={urlToUploadedFile(data.agencyLicenseUrl, "agency_license")}
          onUpload={handleAgencyLicenseUpload}
          onDelete={handleAgencyLicenseDelete}
          loading={agencyLicenseUploading}
        />
      </div>

      {/* Officer ID Upload */}
      <div>
        <FileUpload
          mode="document"
          multiple={false}
          maxSize={15 * 1024 * 1024} // 15MB
          label={t("verificationOfficerId")}
          helperText={t("verificationOfficerIdHint")}
          value={urlToUploadedFile(data.officerIdUrl, "officer_id")}
          onUpload={handleOfficerIdUpload}
          onDelete={handleOfficerIdDelete}
          loading={officerIdUploading}
        />
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>{t("note")}:</strong> {t("verificationDocumentNote")}
        </p>
      </div>
    </div>
  );
});
